<template>
  <s-row>
    <s-col span="1">
      <div>
        <p class="result-text">Results</p>
      </div>
    </s-col>
    <s-col span="9">
      <div class="padding-top">
        <span class="recoverability recoverability-zero border-dark"></span
        ><span class="text-center">0% Recoverable</span
        ><span class="recoverability recoverability-partial border-dark"></span>Partial
        Recoverable
        <span class="recoverability recoverability-full border-dark"></span>Fully
        Recoverable
        <span class="recoverability recoverability-unsupported border-dark"></span>Unsupported
      </div>
    </s-col>
    <s-col span="2">
      <h3>
        <a class="right">
          <button
            class="ghost icon-leading pad-top-xs text-xs"
            @click="onDownloadVATRecoverability"
            title="download matrix"
            link
          >
            <s-icon name="download"></s-icon>
            Download this matrix
          </button>
        </a>
      </h3>
    </s-col>
    <s-col span="12">
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th class="jurisdiction-header">COUNTRY</th>
              <th v-for="(taxCode, index) in taxcodes" :key="index">
                <div>{{ taxCode }}</div>
                <p
                  v-if="
                    !showFullDescription &&
                    taxcodeDetails[taxCode] &&
                    taxcodeDetails[taxCode].description &&
                    taxcodeDetails[taxCode].description.length >
                      descriptionLengthLimit
                  "
                  class="taxcode-desc margin-top-none"
                >
                  {{ getShortTaxcodeDescription(taxCode) }}
                  <a @click="seeMore(taxCode)">...See more</a>
                </p>
                <p v-else class="taxcode-desc margin-top-none">
                  {{ taxcodeDetails[taxCode]?.description || "" }}
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in getTableRows()" :key="row.countryCode">
              <td class="jurisdiction-cell">
                <s-box>{{ row.countryName }}</s-box>
              </td>
              <td v-for="(taxCode, index) in taxcodes" :key="index">
                <s-box class="big-cell">
                  <div v-for="(rule, ruleIndex) in getAllRecoverabilityRules(taxCode, row.countryName)" 
                       :key="ruleIndex"
                       :class="['rule-item', getRecoverabilityClassForRule(rule)]"
                       @click="showDetailsDialogForRule(taxCode, row.countryName, ruleIndex)"
                       style="cursor: pointer;"
                  >
                    <div class="rule-percentage">
                      {{ getDisplayRecoverabilityPercentageForRule(rule) }}
                    </div>
                    <div class="additional-info">
                      <div v-if="rule.attributes_clean || rule.attributes" class="rule-attributes">
                        {{ rule.attributes_clean || rule.attributes }}
                      </div>
                    </div>
                  </div>
                  <div v-if="getAllRecoverabilityRules(taxCode, row.countryName).length === 0 && isUnsupportedForCountry(taxCode, row.countryName)"
                       class="rule-item recoverability-unsupported"
                  >
                    <div class="rule-percentage">Unsupported</div>
                  </div>
                </s-box>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </s-col>
    
    <s-dialog
      id="vat-recoverability-details"
      aria-modal="true"
      ref="vat-recoverability-details-dialog"
      noscroll=""
    >
      <div slot="header" id="dialog-title">VAT recoverability details</div>
      <div slot="body">
        <s-container>
          <s-row class="dialog-row">
            <s-col span="4" class="dialog-label font-semibold">Country</s-col>
            <s-col span="8" class="dialog-value">{{ selectedDetails.country_name || "" }}</s-col>
          </s-row>
          <s-row class="dialog-row">
            <s-col span="4" class="dialog-label font-semibold">Tax code</s-col>
            <s-col span="8" class="dialog-value">
              {{ selectedDetails.avatax_code || "" }}
              <span v-if="taxcodeDetails[selectedDetails.avatax_code]">
                ({{ taxcodeDetails[selectedDetails.avatax_code].description || "" }})
              </span>
            </s-col>
          </s-row>
          <s-row class="dialog-row">
            <s-col span="4" class="dialog-label font-semibold">VAT recoverability</s-col>
            <s-col span="8" class="dialog-value">
              <span v-if="selectedDetails.recoverability_percentage && selectedDetails.recoverability_percentage !== '' && selectedDetails.recoverability_percentage !== 'Unsupported'">
                {{ formatRecoverabilityPercentage(selectedDetails.recoverability_percentage) }}
              </span>
              <span v-else>Unsupported</span>
            </s-col>
          </s-row>
          <s-row class="dialog-row">
            <s-col span="4" class="dialog-label font-semibold">Legislation summary</s-col>
            <s-col span="8" class="dialog-value">
              <p v-if="selectedDetails.legislation_description" class="dialog-text">{{ selectedDetails.legislation_description }}</p>
              <p v-else class="dialog-text">No legislation description available.</p>
            </s-col>
          </s-row>
          <s-row class="dialog-row">
            <s-col span="4" class="dialog-label font-semibold">
              <span 
                @click="toggleAttributesExpanded" 
                class="expandable-label"
              >
                <s-icon 
                  :name="attributesExpanded ? 'chevron-down' : 'chevron-right'" 
                  class="expand-icon"
                ></s-icon>
                Attributes
              </span>
            </s-col>
            <s-col span="8" class="dialog-value">
              <p v-if="!attributesExpanded && selectedDetails.attributes" class="dialog-text attributes-preview">
                Click to expand...
              </p>
              <p v-else-if="attributesExpanded && selectedDetails.attributes" class="dialog-text">
                {{ selectedDetails.attributes }}
              </p>
              <p v-else class="dialog-text">No attributes available.</p>
            </s-col>
          </s-row>
        </s-container>
      </div>
      <div slot="footer">
        <button class="primary small" @click="closeDetailsDialog()">Done</button>
      </div>
    </s-dialog>
  </s-row>
</template>
<script lang="ts">
import { Component, Vue, Prop, Watch, Provide } from "vue-property-decorator";
import dayjs from "dayjs";

interface TaxCode {
  code: string;
  description: string;
  additionalDescription: string;
  categories: string[];
}

interface VATRecoverabilityRule {
  country_code: string;
  country_name: string;
  avatax_code: string;
  recoverability_percentage: string;
  legislation_description: string;
  attributes: string;
  attributes_clean?: string;
  jurisdiction_name?: string;
}

@Component
export default class VATRecoverabilityTable extends Vue {
  @Provide("$api") $api: any;
  @Prop({ required: true }) private taxcodes!: string[];
  @Prop({ required: true }) private countries!: string[];
  @Prop({ required: true }) private codeToCountryRecoverability!: {
    [taxCode: string]: { [country: string]: VATRecoverabilityRule[] };
  };
  @Prop({ required: false }) private allRegionsData?: Array<{label: string, value: string}>;

  taxcodeDetails = {};
  descriptionLengthLimit = 80;
  showFullDescription = false;
  attributesExpanded = false;
  selectedDetails: VATRecoverabilityRule = {
    country_code: "",
    country_name: "",
    avatax_code: "",
    recoverability_percentage: "",
    legislation_description: "",
    attributes: "",
    attributes_clean: "",
  };

  @Watch("taxcodes")
  public updateTaxcodes(newTaxcodes: string[]) {
    this.taxcodes = newTaxcodes;
    this.updateTaxcodeDetails();
  }

  @Watch("countries")
  public updateCountries(newCountries: string[]) {
    this.countries = newCountries;
  }

  @Watch("codeToCountryRecoverability")
  public updateCodeToCountryRecoverability(newMappings: {
    [taxCode: string]: { [country: string]: VATRecoverabilityRule[] };
  }) {
    this.codeToCountryRecoverability = newMappings;
  }

  @Watch("allRegionsData")
  public updateAllRegionsData(newData: Array<{label: string, value: string}>) {
    this.$forceUpdate();
  }

  getTableRows(): Array<{countryName: string, countryCode: string}> {
    const rows: Array<{countryName: string, countryCode: string}> = [];
    const countryCodeToName = new Map<string, string>();
    const processedCountries = new Set<string>();
    
    if (this.taxcodes && this.taxcodes.length > 0) {
      this.taxcodes.forEach((taxCode) => {
        if (this.codeToCountryRecoverability && this.codeToCountryRecoverability[taxCode]) {
          Object.keys(this.codeToCountryRecoverability[taxCode]).forEach((countryName) => {
            const rules = this.codeToCountryRecoverability[taxCode][countryName];
            if (Array.isArray(rules) && rules.length > 0) {
              const countryCode = rules[0].country_code;
              let preferredName = countryName;
              if (this.allRegionsData && this.allRegionsData.length > 0) {
                const region = this.allRegionsData.find(r => r.value === countryCode);
                if (region) {
                  preferredName = region.label;
                }
              }
              countryCodeToName.set(countryCode, preferredName);
            }
          });
        }
      });
    }
    
    if (this.countries && this.countries.length > 0) {
      this.countries.forEach((countryCode) => {
        let countryName: string | null = null;
        
        if (this.allRegionsData && this.allRegionsData.length > 0) {
          const region = this.allRegionsData.find(r => r.value === countryCode);
          if (region) {
            countryName = region.label;
          }
        }
        
        if (!countryName) {
          countryName = this.getCountryNameFromCode(countryCode);
        }
        
        if (!countryName) {
          countryName = countryCode;
        }
        
        countryCodeToName.set(countryCode, countryName);
      });
    }
    
    countryCodeToName.forEach((countryName, countryCode) => {
      if (!processedCountries.has(countryCode)) {
        rows.push({
          countryName: countryName,
          countryCode: countryCode
        });
        processedCountries.add(countryCode);
      }
    });
    
    return rows.sort((a, b) => a.countryName.localeCompare(b.countryName));
  }

  getCountryNames() {
    const rows = this.getTableRows();
    const uniqueCountries = new Set<string>();
    rows.forEach(row => uniqueCountries.add(row.countryName));
    return Array.from(uniqueCountries).sort();
  }

  getCountryNameFromCode(countryCode: string): string | null {
    if (this.allRegionsData) {
      const region = this.allRegionsData.find(r => r.value === countryCode);
      if (region) {
        return region.label;
      }
    }
    
    for (const taxCode of this.taxcodes) {
      if (this.codeToCountryRecoverability[taxCode]) {
        for (const countryName of Object.keys(this.codeToCountryRecoverability[taxCode])) {
          const rules = this.codeToCountryRecoverability[taxCode][countryName];
          if (Array.isArray(rules) && rules.length > 0) {
            const rule = rules[0];
            if (rule && rule.country_code === countryCode) {
              return countryName;
            }
          }
        }
      }
    }
    return null;
  }

  getShortTaxcodeDescription(taxcode) {
    const description: string = this.taxcodeDetails[taxcode]?.description || "";
    return description.substr(0, this.descriptionLengthLimit - 15);
  }

  seeMore() {
    this.showFullDescription = true;
  }

  mounted() {
    this.updateTaxcodeDetails();
  }

  public updateTaxcodeDetails() {
    this.$api
      .post("/api/taxcodes/", this.taxcodes)
      .then((response) => {
        const taxcodesData: TaxCode[] = response.data;
        taxcodesData.forEach((taxcodeData) =>
          Vue.set(this.taxcodeDetails, taxcodeData.code, taxcodeData)
        );
        this.$store.commit("stopLoading");
      })
      .catch((error) => this.$store.commit("stopLoading"));
  }

  public onDownloadVATRecoverability() {
    this.$api
      .post(
        "/api/vat-recoverability/",
        {
          taxcodes: this.taxcodes,
          country_codes: this.countries,
        },
        {
          responseType: "arraybuffer",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "vat_recoverability.xlsx");
        document.body.appendChild(link);
        link.click();
        this.captureDownloadVATRecoverability();
      })
      .catch((error) => this.$store.commit("stopLoading"));
  }

  public captureDownloadVATRecoverability() {
    if (this.$store.getters.analyticsEnabled) {
      window._satellite.track("vat_recoverability_build_download");
    }
  }

  public getRecoverabilityClass(
    taxCode: string,
    country: string,
    ruleIndex = 0
  ): string {
    if (this.isUnsupported(taxCode, country, ruleIndex)) {
      return "recoverability-unsupported";
    }
    
    const recoverabilityRule = this.getRecoverabilityByCodeAndCountry(
      taxCode,
      country,
      ruleIndex
    );
    const percentage = parseFloat(recoverabilityRule.recoverability_percentage || "0");
    
    if (percentage === 0) return "recoverability-zero";
    if (percentage > 0 && percentage < 100) return "recoverability-partial";
    if (percentage === 100) return "recoverability-full";
    return "";
  }

  public isUnsupported(taxCode: string, country: string, ruleIndex = 0): boolean {
    const rules = this.codeToCountryRecoverability[taxCode]?.[country];
    const hasData = Array.isArray(rules) && 
                    rules.length > ruleIndex &&
                    rules[ruleIndex] &&
                    rules[ruleIndex].recoverability_percentage !== "";
    
    let countryCode: string | null = null;
    if (this.countries && this.countries.includes(country)) {
      countryCode = country;
    } else {
      countryCode = this.getCountryCodeFromName(country);
    }
    
    const wasRequested = countryCode && this.countries && this.countries.includes(countryCode);
    
    return Boolean(wasRequested) && !hasData;
  }

  public getCountryCodeFromName(countryName: string): string | null {
    if (this.allRegionsData) {
      const region = this.allRegionsData.find(r => r.label === countryName);
      if (region) {
        return region.value;
      }
    }
    
    for (const taxCode of this.taxcodes) {
      if (this.codeToCountryRecoverability[taxCode]) {
        const rules = this.codeToCountryRecoverability[taxCode][countryName];
        if (Array.isArray(rules) && rules.length > 0) {
          const rule = rules[0];
          if (rule && rule.country_code) {
            return rule.country_code;
          }
        }
      }
    }
    return null;
  }

  public getDisplayRecoverabilityPercentage(taxCode: string, country: string, ruleIndex = 0): string {
    if (this.isUnsupported(taxCode, country, ruleIndex)) {
      return "Unsupported";
    }
    
    const rule = this.getRecoverabilityByCodeAndCountry(taxCode, country, ruleIndex);
    const percentage = rule.recoverability_percentage;
    
    if (!percentage || percentage === "") {
      return "Unsupported";
    }
    
    return `${percentage}%`;
  }

  public getShortLegislationDescription(taxCode: string, country: string, ruleIndex = 0): string {
    const rule = this.getRecoverabilityByCodeAndCountry(taxCode, country, ruleIndex);
    const desc = rule.legislation_description || "";
    if (desc.length > 100) {
      return desc.substr(0, 100) + "...";
    }
    return desc;
  }

  public showDetailsDialog(taxCode: string, country: string, ruleIndex = 0) {
    const rule = this.getRecoverabilityByCodeAndCountry(taxCode, country, ruleIndex);
    this.selectedDetails = {
      ...rule,
      avatax_code: taxCode,
      country_name: country,
    };
    (this.$refs["vat-recoverability-details-dialog"] as any).showModal();
  }

  public closeDetailsDialog() {
    (this.$refs["vat-recoverability-details-dialog"] as any).close();
  }

  public toggleAttributesExpanded() {
    this.attributesExpanded = !this.attributesExpanded;
  }

  public getRecoverabilityByCodeAndCountry(
    taxCode: string,
    country: string,
    ruleIndex = 0
  ) {
    if (
      this.codeToCountryRecoverability[taxCode] &&
      this.codeToCountryRecoverability[taxCode][country] &&
      Array.isArray(this.codeToCountryRecoverability[taxCode][country])
    ) {
      const rules = this.codeToCountryRecoverability[taxCode][country];
      if (rules.length > ruleIndex) {
        return rules[ruleIndex];
      }
    }
    return {
      country_code: "",
      country_name: country,
      avatax_code: taxCode,
      recoverability_percentage: "",
      legislation_description: "",
      attributes: "",
    };
  }

  public getAllRecoverabilityRules(taxCode: string, country: string): VATRecoverabilityRule[] {
    if (
      this.codeToCountryRecoverability[taxCode] &&
      this.codeToCountryRecoverability[taxCode][country] &&
      Array.isArray(this.codeToCountryRecoverability[taxCode][country])
    ) {
      return this.codeToCountryRecoverability[taxCode][country];
    }
    return [];
  }

  public getRecoverabilityClassForRule(rule: VATRecoverabilityRule): string {
    if (!rule || !rule.recoverability_percentage || rule.recoverability_percentage === "" || rule.recoverability_percentage === "Unsupported") {
      return "recoverability-unsupported";
    }
    
    const percentage = parseFloat(rule.recoverability_percentage || "0");
    
    if (percentage === 0) return "recoverability-zero";
    if (percentage > 0 && percentage < 100) return "recoverability-partial";
    if (percentage === 100) return "recoverability-full";
    return "";
  }

  public getDisplayRecoverabilityPercentageForRule(rule: VATRecoverabilityRule): string {
    if (!rule || !rule.recoverability_percentage || rule.recoverability_percentage === "" || rule.recoverability_percentage === "Unsupported") {
      return "Unsupported";
    }
    return `${rule.recoverability_percentage}%`;
  }

  public getShortLegislationDescriptionForRule(rule: VATRecoverabilityRule): string {
    const desc = rule.legislation_description || "";
    if (desc.length > 100) {
      return desc.substr(0, 100) + "...";
    }
    return desc;
  }

  public formatRecoverabilityPercentage(percentage: string): string {
    if (!percentage || percentage === "" || percentage === "Unsupported") {
      return "Unsupported";
    }
    let cleaned = percentage.toString().trim().replace(/\.$/, "");
    const num = parseFloat(cleaned);
    if (isNaN(num)) {
      return "Unsupported";
    }
    if (num % 1 === 0) {
      return `${num}%`;
    } else {
      return `${num}%`.replace(/\.?0+%$/, "%");
    }
  }

  public showDetailsDialogForRule(taxCode: string, country: string, ruleIndex: number) {
    const rules = this.getAllRecoverabilityRules(taxCode, country);
    if (rules.length > ruleIndex) {
      const rule = rules[ruleIndex];
      this.selectedDetails = {
        ...rule,
        avatax_code: taxCode,
        country_name: country,
      };
      this.attributesExpanded = false; // Reset expanded state when opening dialog
      (this.$refs["vat-recoverability-details-dialog"] as any).showModal();
    }
  }

  public isUnsupportedForCountry(taxCode: string, country: string): boolean {
    const rules = this.getAllRecoverabilityRules(taxCode, country);
    const hasData = rules.length > 0 && rules.some(rule => 
      rule.recoverability_percentage && 
      rule.recoverability_percentage !== "" && 
      rule.recoverability_percentage !== "Unsupported"
    );
    
    let countryCode: string | null = null;
    if (this.countries && this.countries.includes(country)) {
      countryCode = country;
    } else {
      countryCode = this.getCountryCodeFromName(country);
    }
    
    const wasRequested = countryCode && this.countries && this.countries.includes(countryCode);
    
    return Boolean(wasRequested) && !hasData;
  }
}
</script>
<style scoped lang="scss">
.table-container {
  overflow-x: scroll;
  padding: 0px;
  border: 0.1rem solid var(--color-gray-medium);
  margin-bottom: 50px;
}

table {
  margin-top: 0px;
  table-layout: fixed;
  width: auto;
}

table td {
  width: 320px;
  font-weight: bold;
  font-size: 0.865rem;
  border-bottom: 5px solid var(--color-white);
  padding-right: 0px;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 5px;

  s-box.big-cell {
    min-height: 80px;
    width: 320px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  s-box {
    .additional-info {
      div {
        line-height: 0.7rem;
        font-weight: normal;
        font-size: 0.67rem;
      }
      line-height: 0.7rem;
      font-weight: normal;
      font-size: 0.67rem;
      height: 3rem;
    }
  }
}

thead {
  background-color: var(--color-gray-lightest);
  font-weight: bold;
  font-size: 0.865rem;
  border-bottom: 5px solid var(--color-white);
  border-top: none;

  th {
    .taxcode-desc {
      a {
        display: initial !important;
      }

      font-weight: 500;
      font-size: 0.78rem;
      white-space: initial;
    }

    padding-top: 12px;
    padding-bottom: 12px;
    vertical-align: top;
    border-top: none;
    width: 320px;
  }

  .jurisdiction-header {
    color: var(--color-gray-dark);
    font-size: 0.675rem;
    min-width: 250px;
  }
}

.jurisdiction-cell {
  background-color: var(--color-gray-lightest);

  s-box {
    background-color: var(--color-gray-lightest);
    border: none;
  }
}

s-col {
  padding-bottom: 0.1rem;
}

button.ghost {
  color: var(--color-blue-dark);
}

button.primary {
  font-size: 0.782rem;
  flex: auto;
}

.result-text {
  font-size: 1.4rem;
  font-weight: 500;
  margin-top: 20px;
}

.recoverability {
  margin: 0px 5px 2px 20px;
  display: inline-block;
  border-radius: 5px;
  min-width: 20px;
  min-height: 20px;
  vertical-align: middle;
}

.recoverability-zero {
  background-color: var(--color-red-lightest);
  border: 1px solid var(--color-red-darker);
}

.recoverability-partial {
  background-color: var(--color-yellow-lightest);
  border: 1px solid var(--color-yellow-darker);
}

.recoverability-full {
  background-color: var(--color-green-lightest);
  border: 1px solid var(--color-green-darker);
}

.recoverability-unsupported {
  background-color: var(--color-red-lightest);
  border: 1px solid var(--color-red-darker);
}

.padding-top {
  padding-top: 20px;
}

table td s-box.big-cell {
  cursor: default;
  
  .rule-item {
    cursor: pointer;
  }
}

.font-semibold {
  font-weight: 600;
}

.text-sm {
  font-size: 0.875rem;
}

.border-dark {
  border-width: 2px;
}

.rule-item {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid var(--color-gray-medium);
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
  
  .rule-percentage {
    font-weight: bold;
    font-size: 0.9rem;
    margin-bottom: 4px;
  }
  
  .rule-description {
    font-size: 0.75rem;
    line-height: 1.2;
    color: var(--color-gray-dark);
    margin-bottom: 4px;
  }
  
  .rule-attributes {
    font-size: 0.7rem;
    color: var(--color-gray-dark);
    font-style: italic;
    word-break: break-word;
  }
  
  &.recoverability-zero {
    background-color: var(--color-red-lightest);
    border-color: var(--color-red-darker);
  }
  
  &.recoverability-partial {
    background-color: var(--color-yellow-lightest);
    border-color: var(--color-yellow-darker);
  }
  
  &.recoverability-full {
    background-color: var(--color-green-lightest);
    border-color: var(--color-green-darker);
  }
  
  &.recoverability-unsupported {
    background-color: var(--color-gray-lightest);
    border-color: var(--color-gray-medium);
  }
}

.dialog-row {
  margin-bottom: 16px;
  align-items: flex-start;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.dialog-label {
  min-width: 140px;
  padding-right: 16px;
  vertical-align: top;
  flex-shrink: 0;
}

.dialog-value {
  word-wrap: break-word;
  word-break: break-word;
  vertical-align: top;
  padding-left: 0;
}

.dialog-text {
  margin: 0;
  line-height: 1.5;
  word-wrap: break-word;
  word-break: break-word;
}

s-container {
  s-row {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 16px;
    
    s-col {
      display: flex;
      align-items: flex-start;
    }
  }
}

.expandable-label {
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--color-blue-dark);
  
  &:hover {
    text-decoration: underline;
  }
  
  .expand-icon {
    margin-right: 4px;
    font-size: 0.75rem;
  }
}

.attributes-preview {
  color: var(--color-gray-dark);
  font-style: italic;
  cursor: pointer;
}
</style>
